import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MessageCircle, Phone, Loader2, MapPin, Mail, Search, Download, Users, Home as HomeIcon,
} from 'lucide-react';
import AdminShell from '../components/AdminShell';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cleanPhone = (p) => (p || '').replace(/\D/g, '');

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const AdminLeads = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ leads: [], count: 0, totals: { whatsapp: 0, neighbourhood: 0, waitlist: 0 } });
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) { navigate('/admin/login'); return; }
      const res = await axios.get(`${API}/admin/leads`, {
        params: { kind, limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load leads');
      }
    } finally {
      setLoading(false);
    }
  }, [kind, navigate]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = data.leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.name || '').toLowerCase().includes(s)
      || (l.phone || '').toLowerCase().includes(s)
      || (l.city || '').toLowerCase().includes(s)
      || (l.neighbourhood || '').toLowerCase().includes(s)
      || (l.email || '').toLowerCase().includes(s)
      || (l.detail || '').toLowerCase().includes(s);
  });

  const openWhatsApp = (l) => {
    const phone = cleanPhone(l.phone);
    if (phone.length < 10) { toast.error('Invalid phone number'); return; }
    const prefix = l.kind === 'neighbourhood'
      ? `Hi ${l.name}, this is Elderra (+91 89204 55730). Thanks for inviting us to ${l.neighbourhood || 'your neighbourhood'}!`
      : `Hi ${l.name}, this is Elderra (+91 89204 55730). Welcome to our community — glad to have you!`;
    const msg = encodeURIComponent(prefix);
    const to = phone.length === 10 ? `91${phone}` : phone;
    window.open(`https://wa.me/${to}?text=${msg}`, '_blank', 'noopener');
  };

  const downloadCSV = () => {
    if (filtered.length === 0) { toast.error('No leads to export'); return; }
    const headers = ['Kind', 'Name', 'Phone', 'Email', 'City', 'Neighbourhood / Group', 'Status', 'Notes', 'Created At'];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filtered.map((l) => [
      l.kind_label,
      l.name,
      l.phone,
      l.email || '',
      l.city || '',
      l.detail || '',
      l.status || '',
      l.notes || '',
      l.created_at || '',
    ].map(esc).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().split('T')[0];
    const filename = `elderra-leads-${kind}-${today}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? '' : 's'}`);
  };

  const totals = data.totals || { whatsapp: 0, neighbourhood: 0, waitlist: 0 };

  return (
    <AdminShell>
      <SEO title="Leads - Elderra Admin" description="WhatsApp joins and neighbourhood invites." />

      <div data-testid="admin-leads-page" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Leads</div>
        <h1 style={{ margin: '4px 0 6px', fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>All Leads</h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>WhatsApp group joins and neighbourhood invites — ready to call or WhatsApp.</p>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div data-testid="leads-stat-whatsapp" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={18} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>WhatsApp Joins</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totals.whatsapp}</div>
          </div>
        </div>
        <div data-testid="leads-stat-neighbourhood" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fff0f5', color: '#e5266a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HomeIcon size={18} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Neighbourhood Invites</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totals.neighbourhood}</div>
          </div>
        </div>
        <div data-testid="leads-stat-waitlist" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
            🌱
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>City Waitlist</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totals.waitlist}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div data-testid="leads-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20, background: '#fff', padding: '14px 16px', borderRadius: 12, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 9, padding: 3 }}>
          {[
            { id: 'all', label: `All (${totals.whatsapp + totals.neighbourhood + totals.waitlist})` },
            { id: 'whatsapp', label: `WhatsApp (${totals.whatsapp})` },
            { id: 'neighbourhood', label: `Neighbourhood (${totals.neighbourhood})` },
            { id: 'waitlist', label: `Waitlist (${totals.waitlist})` },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setKind(s.id)}
              data-testid={`filter-kind-${s.id}`}
              style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer',
                background: kind === s.id ? '#0f172a' : 'transparent',
                color: kind === s.id ? '#fff' : '#6b7280',
                borderRadius: 7, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search by name, phone, city, neighbourhood..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search"
            style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
          <span data-testid="leads-total">{filtered.length}</span> of {data.count}
        </div>

        <button
          data-testid="leads-export-csv"
          onClick={downloadCSV}
          disabled={loading || filtered.length === 0}
          title="Download current leads as CSV"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 9,
            background: filtered.length === 0 ? '#f3f4f6' : '#0f172a',
            color: filtered.length === 0 ? '#9ca3af' : '#fff',
            border: 'none', cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}
        >
          <Download size={14} /> Download CSV
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
          <Loader2 size={20} style={{ animation: 'adm-spin 1s linear infinite' }} /> Loading leads...
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="leads-empty" style={{ padding: 60, textAlign: 'center', background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb' }}>
          <Users size={32} style={{ color: '#d1d5db', margin: '0 auto 8px', display: 'block' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>No leads yet.</div>
        </div>
      ) : (
        <div data-testid="leads-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((l) => {
            const isWa = l.kind === 'whatsapp';
            const isWl = l.kind === 'waitlist';
            const Icon = isWa ? Users : isWl ? MapPin : HomeIcon;
            const tint = isWa ? '#dcfce7' : isWl ? '#fef3c7' : '#fff0f5';
            const fg = isWa ? '#16a34a' : isWl ? '#d97706' : '#e5266a';
            return (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: tint, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={18} />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{l.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: tint, color: fg, padding: '2px 7px', borderRadius: 999 }}>{l.kind_label}</span>
                    </div>
                    <div style={{ marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12.5, color: '#4b5563' }}>
                      {l.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Phone size={12} /> {l.phone}</span>}
                      {l.city && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {l.city}{l.neighbourhood ? ` · ${l.neighbourhood}` : ''}</span>}
                      {l.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Mail size={12} /> {l.email}</span>}
                      {!isWa && l.detail && !l.neighbourhood && <span>{l.detail}</span>}
                      {isWa && l.detail && <span style={{ color: '#6b7280' }}>Group: {l.detail}</span>}
                      <span style={{ color: '#9ca3af' }}>{formatWhen(l.created_at)}</span>
                    </div>
                    {l.notes && (
                      <div style={{ marginTop: 6, fontSize: 12.5, color: '#374151', background: '#f9fafb', padding: '6px 10px', borderRadius: 8 }}>
                        {l.notes}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button
                      data-testid={`lead-whatsapp-${l.id}`}
                      onClick={() => openWhatsApp(l)}
                      title="Send WhatsApp"
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                      <MessageCircle size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes adm-spin { to { transform: rotate(360deg); } }`}</style>
    </AdminShell>
  );
};

export default AdminLeads;
